import axios from "axios";
import { authService } from "./authService";

const env = process.env.REACT_APP_ENVIRONMENT;
const apiBaseUrl =
  env === "LOCAL"
    ? process.env.REACT_APP_BACKEND_URL_LOCAL
    : process.env.REACT_APP_BACKEND_URL_PRODUCTION;

const apiClient = axios.create({
  baseURL: apiBaseUrl,
  headers: { "Content-Type": "application/json" },
  timeout: 90000,
});

apiClient.interceptors.request.use(async (config) => {
  const token = await authService.acquireToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Maps EmployeeView (nested) → flat UI object used by the employee components
const mapEmployeeViewToUI = (
  { id, created_at, employee, vacation_requests_count },
  { vacations = [] } = {},
) => ({
  id,
  created_at,
  vacation_requests_count,
  // Work Information
  work_email: employee.work_email ?? "",
  work_phone: employee.work_phone ?? "",
  hire_date: employee.hire_date ?? "",
  status: employee.status ?? "",
  termination_date: employee.termination_date ?? "",
  termination_reason: employee.termination_reason ?? "",
  job_title: employee.job_title ?? "",
  employment_type: employee.employment_type ?? "",
  level: employee.level ?? "",
  department: employee.department ?? "",
  is_hr_manager: employee.is_hr_manager ?? "",
  manager_id: employee.manager_id ?? null,
  manager_name: employee.manager_name ?? "",
  vacation_days_balance: employee.vacation_days_balance ?? null,
  sick_days_balance: employee.sick_days_balance ?? null,
  floating_holiday_balance: employee.floating_holiday_balance ?? null,
  // Personal Information
  name: employee.name ?? "",
  date_of_birth: employee.date_of_birth ?? "",
  ssn: employee.ssn ?? "",
  gender: employee.gender ?? "",
  personal_email: employee.personal_email ?? "",
  personal_phone: employee.personal_phone ?? "",
  address: employee.address ?? "",
  city: employee.city ?? "",
  state: employee.state ?? "",
  zip: employee.zip ?? "",
  country: employee.country ?? "",
  emergency_contact_name: employee.emergency_contact_name ?? "",
  emergency_contact_phone: employee.emergency_contact_phone ?? "",
  // Compensation
  compensation_type: employee.compensation_type ?? "",
  base_rate: employee.base_rate ?? null,
  bank_account: employee.bank_account ?? "",
  vacations,
});

// Maps backend VacationRequestOut → UI vacation object
const mapVacationToUI = (vr) => ({
  id: vr.id,
  employee_id: vr.employee_id,
  type: vr.absence_type ?? "",
  start: vr.start_date ?? "",
  end: vr.end_date ?? "",
  days: vr.business_days ?? "",
  status: vr.status ?? "",
  approved_by: vr.approved_by ?? null,
  approver_name: vr.approver_name ?? "",
  notes: vr.notes ?? "",
  created_at: vr.created_at,
  updated_at: vr.updated_at,
});

class EmployeeService {
  // -------- Employees --------

  /**
   * Builds the nested employee payload from a flat UI data object.
   * Coerces empty strings → null so optional date/int fields
   * (hire_date, termination_date, date_of_birth, manager_id, ...)
   * don't fail Pydantic's Optional[date]/Optional[int] validation.
   */
  _buildEmployeePayload = (flatData) => {
    const nullify = (v) => (v === "" || v === undefined ? null : v);
    return {
      work_email: nullify(flatData.work_email),
      work_phone: nullify(flatData.work_phone),
      hire_date: nullify(flatData.hire_date),
      status: nullify(flatData.status),
      termination_date: nullify(flatData.termination_date),
      termination_reason: nullify(flatData.termination_reason),
      job_title: nullify(flatData.job_title),
      employment_type: nullify(flatData.employment_type),
      level: nullify(flatData.level),
      department: nullify(flatData.department),
      is_hr_manager: nullify(flatData.is_hr_manager),
      manager_id: nullify(flatData.manager_id),
      vacation_days_balance: nullify(flatData.vacation_days_balance),
      sick_days_balance: nullify(flatData.sick_days_balance),
      floating_holiday_balance: nullify(flatData.floating_holiday_balance),
      name: nullify(flatData.name),
      date_of_birth: nullify(flatData.date_of_birth),
      ssn: nullify(flatData.ssn),
      gender: nullify(flatData.gender),
      personal_email: nullify(flatData.personal_email),
      personal_phone: nullify(flatData.personal_phone),
      address: nullify(flatData.address),
      city: nullify(flatData.city),
      state: nullify(flatData.state),
      zip: nullify(flatData.zip),
      country: nullify(flatData.country),
      emergency_contact_name: nullify(flatData.emergency_contact_name),
      emergency_contact_phone: nullify(flatData.emergency_contact_phone),
      compensation_type: nullify(flatData.compensation_type),
      base_rate: nullify(flatData.base_rate),
      bank_account: nullify(flatData.bank_account),
    };
  };

  /**
   * List / search employees.
   * @param {string} search        - free-text (name, email, phone, department, job title)
   * @param {number} limit
   * @param {number} skip
   * @param {object} filters       - { status?: string[], department?: string[] }
   * @returns {{ items: EmployeeView[], total: number, limit: number, skip: number }}
   */
  getEmployees = async (search = "", limit = 50, skip = 0, filters = {}) => {
    const params = new URLSearchParams({
      limit: String(limit),
      skip: String(skip),
    });
    if (search) params.append("search", search);
    filters.status?.forEach((s) => params.append("status", s));
    filters.department?.forEach((d) => params.append("department", d));
    const { data } = await apiClient.get(`/api/employees?${params}`);
    return data; // { items: EmployeeView[], total, limit, skip }
  };

  /**
   * Fetch a single employee with vacation requests hydrated.
   * Returns a flat UI object.
   */
  getEmployee = async (employeeId) => {
    const [{ data: ev }, vacations] = await Promise.all([
      apiClient.get(`/api/employees/${employeeId}`),
      this.getVacations(employeeId),
    ]);
    return mapEmployeeViewToUI(ev, { vacations });
  };

  /**
   * Returns a flat list of { id, name } for use in manager select dropdowns.
   */
  getManagers = async (search = "", levels = []) => {
    const params = new URLSearchParams({ limit: "100", skip: "0" });
    if (search) params.append("search", search);
    const { data } = await apiClient.get(`/api/employees?${params}`);
    return (data.items || [])
      .filter(
        (item) => levels.length === 0 || levels.includes(item.employee?.level),
      )
      .map((item) => ({
        id: item.id,
        name: item.employee?.name ?? "",
      }));
  };

  /**
   * Create an employee from a flat UI data object.
   * Handles empty-string → null coercion for optional date/numeric fields.
   * payload shape: { employee: { name, work_email, hire_date, status, ... } }
   */
  createEmployee = async (flatData) => {
    const payload = { employee: this._buildEmployeePayload(flatData) };
    try {
      const { data } = await apiClient.post("/api/employees", payload);
      return mapEmployeeViewToUI(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((e) => `${e.loc?.join(".")} — ${e.msg}`).join("; ")
        : detail || "Failed to create employee";
      throw new Error(msg);
    }
  };

  /**
   * Update an employee from a flat UI data object.
   * Handles empty-string → null coercion for optional numeric fields.
   */
  updateEmployee = async (employeeId, flatData) => {
    const payload = { employee: this._buildEmployeePayload(flatData) };
    try {
      const { data } = await apiClient.put(
        `/api/employees/${employeeId}`,
        payload,
      );
      return mapEmployeeViewToUI(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((e) => `${e.loc?.join(".")} — ${e.msg}`).join("; ")
        : detail || "Failed to update employee";
      throw new Error(msg);
    }
  };

  /** Delete an employee (cascades to vacation requests). */
  deleteEmployee = async (employeeId) => {
    const res = await apiClient.delete(`/api/employees/${employeeId}`);
    return res.status === 204 ? { ok: true } : { ok: false };
  };

  /**
   * Export employees as an Excel blob.
   * @param {object} opts - { search?, status?: string[], department?: string[] }
   */
  exportEmployees = async ({ search, status, department } = {}) => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    status?.forEach((s) => params.append("status", s));
    department?.forEach((d) => params.append("department", d));

    const res = await apiClient.get(
      `/api/employees/export?${params.toString()}`,
      {
        responseType: "blob",
        headers: {
          Accept:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
        validateStatus: (s) => s >= 200 && s < 300,
      },
    );

    const ct = (res.headers && res.headers["content-type"]) || "";
    const isExcel = ct.includes(
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    );
    if (!isExcel) {
      try {
        const text = await new Response(res.data).text();
        throw new Error(
          `Export failed. Content-Type: ${ct}. Body: ${text.slice(0, 300)}`,
        );
      } catch {
        throw new Error(`Export failed. Content-Type: ${ct}.`);
      }
    }
    return res.data; // Blob
  };

  /**
   * Export vacation requests as an Excel blob.
   * @param {object} opts - { employeeIds?: number[] } — omit to export all
   */
  exportVacations = async ({ employeeIds } = {}) => {
    const params = new URLSearchParams();
    employeeIds?.forEach((id) => params.append("employee_ids", id));

    const res = await apiClient.get(
      `/api/vacation-requests/export?${params.toString()}`,
      {
        responseType: "blob",
        headers: {
          Accept:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
        validateStatus: (s) => s >= 200 && s < 300,
      },
    );

    const ct = (res.headers && res.headers["content-type"]) || "";
    const isExcel = ct.includes(
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    );
    if (!isExcel) {
      try {
        const text = await new Response(res.data).text();
        throw new Error(
          `Export failed. Content-Type: ${ct}. Body: ${text.slice(0, 300)}`,
        );
      } catch {
        throw new Error(`Export failed. Content-Type: ${ct}.`);
      }
    }
    return res.data; // Blob
  };

  // -------- Vacation Requests --------

  /**
   * List vacation requests for an employee, mapped to UI shape.
   * UI shape: { id, type, start, end, days, status, approved_by, approver_name, notes }
   */
  getVacations = async (employeeId, status = "") => {
    const params = new URLSearchParams();
    if (status) params.append("status", status);
    const { data } = await apiClient.get(
      `/api/employees/${employeeId}/vacation-requests?${params}`,
    );
    return data.map(mapVacationToUI);
  };

  /**
   * Create a vacation request.
   * UI payload: { type, start, end, days, notes? }
   */
  createVacation = async (
    employeeId,
    { type, start, end, days, notes = "" },
  ) => {
    const payload = {
      absence_type: type,
      start_date: start || null,
      end_date: end || null,
      business_days: days ? Number(days) : null,
      status: "Pending",
      notes: notes || null,
    };
    try {
      const { data } = await apiClient.post(
        `/api/employees/${employeeId}/vacation-requests`,
        payload,
      );
      return mapVacationToUI(data);
    } catch (err) {
      const msg =
        err.response?.data?.detail || "Failed to create vacation request";
      throw new Error(msg);
    }
  };

  /**
   * Update a vacation request.
   * UI payload: { type, start, end, days, status?, approved_by?, notes? }
   */
  updateVacation = async (
    employeeId,
    vrId,
    { type, start, end, days, status, approved_by, notes },
  ) => {
    const payload = {
      absence_type: type,
      start_date: start || null,
      end_date: end || null,
      business_days: days ? Number(days) : null,
      status: status || null,
      approved_by: approved_by ?? null,
      notes: notes || null,
    };
    try {
      const { data } = await apiClient.put(
        `/api/employees/${employeeId}/vacation-requests/${vrId}`,
        payload,
      );
      return mapVacationToUI(data);
    } catch (err) {
      const msg =
        err.response?.data?.detail || "Failed to update vacation request";
      throw new Error(msg);
    }
  };

  /** Delete a vacation request. */
  deleteRequest = async (employeeId, vrId) => {
    const res = await apiClient.delete(
      `/api/employees/${employeeId}/vacation-requests/${vrId}`,
    );
    return res.status === 204 ? { ok: true } : { ok: false };
  };
}

export const employeeService = new EmployeeService();
